/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import {
  LoginAuthUserInputFields,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { DATALAYER_PAGE_EVENTS } from '../constants/analytics-constants';
import {
  DashboardDataResponse,
  loginAndGetDashboardResponse
} from '../models/login-authentication-screen.interface';
import { FacadeSupportService } from './facade-support.service';

@Injectable({
  providedIn: 'root'
})
export class FacadeHelperService extends FacadeSupportService {
  loginDashboardResponse: loginAndGetDashboardResponse =
    {} as loginAndGetDashboardResponse;

  getDashBrdDataBffCall(
    userInputFields: LoginAuthUserInputFields | undefined,
    tokenResponse: string,
    authenticationConfig: OauthAEMConfig,
    fromApp = '',
    standalonePage = false
  ) {
    const excludeList = authenticationConfig.BFF_CODES_TO_IGNORE;
    this.pingAdapter.redirectUrl = this.redirectUrl;
    this.loginResponseService
      .getLoginResponse(
        authenticationConfig,
        this.adapter.getNewBrdDashDataRequest(
          userInputFields,
          tokenResponse,
          fromApp,
          standalonePage,
          authenticationConfig?.TOKEN_HEADER
            ? authenticationConfig.TOKEN_HEADER
            : false
        ),
        authenticationConfig?.TOKEN_HEADER ? tokenResponse : ''
      )
      .subscribe((response) => {
        if (response?.errors) {
          const [{ code = '', description = '' }] = response.errors;
          this.loggingService.log(`${code}: ${description}`);
          if (
            this.loginAuthenticationHelperService.checkForExcludeList(
              excludeList,
              response
            )
          ) {
            this.pingAdapter.makeStartSloCall(authenticationConfig);
          } else {
            const errorResponse: { errorId: string; accountLocked?: boolean } =
              { errorId: `ERROR_MSG_${response?.errors[0]?.code}` };
            this.updateLoaderAndDisableButton(false);
            this.displayPingError(errorResponse);
          }
          this.dataLayerServcie.updateAdobeDataLayerError(
            response?.smNumber,
            response?.membershipLevel,
            response?.smeCardTypes,
            response?.smBalance,
            response?.primaryEmailAddress
          );
        } else {
          this.loginDashboardResponse = response;
          if (
            this.loginAuthenticationHelperService.checkForNotLoggedin(response)
          ) {
            this.pingAdapter.makeStartSloCall(authenticationConfig);
          } else {
            this.disableSpinnerForAccountRecovery(response);
            this.processLoginDashboard(
              response,
              authenticationConfig,
              standalonePage,
              tokenResponse
            );
          }
        }
      });
  }

  disableSpinnerForAccountRecovery(response: loginAndGetDashboardResponse) {
    if (!response.hasEmail || !response.isEmailValid || !response.hasSQA)
      this.updateLoaderAndDisableButton(false);
  }

  processLoginDashboard(
    response: loginAndGetDashboardResponse,
    authenticationConfig: OauthAEMConfig,
    standalonePage = false,
    tokenResponse?: string
  ) {
    this.dataLayerServcie.updateAdobeDataLayerLoginSuccess(
      response?.smNumber,
      response?.membershipLevel,
      response?.smeCardTypes,
      response?.smBalance,
      response?.primaryEmailAddress
    );
    this.dataLayerServcie.triggerLoginDataLayerEvents(
      DATALAYER_PAGE_EVENTS.LOGIN_SUCCESS
    );
    this.dataLayerServcie.updateMedallionStatusAndSkyMilesNumber(
      response?.smNumber,
      response?.membershipLevel
    );
    this.pingAdapter.fromApplication = this.fromApplication;
    this.pingAdapter.redirectUrl = this.redirectUrl;
    this.pingAdapter.processLoginDashboardSuccessResponse(
      response,
      authenticationConfig,
      standalonePage,
      tokenResponse
    );
  }

  async loginPostWithBFF(userInputFields?: LoginAuthUserInputFields) {
    if (
      this.loginAuthenticationHelperService.validityCheckForFormSubmit(
        userInputFields,
        this.authenticationConfig
      )
    ) {
      const tokenResponse =
        await this.pingAdapter.authenicateUserAndGetAccessToken(
          userInputFields,
          this.authenticationConfig
        );
      this.loginRememberMeUtils.sendTokenServiceWorker(
        this.authenticationConfig,
        tokenResponse
      );
      this.loginAuthenticationHelperService.setLocalStorageForPersistentLogin(
        tokenResponse?.encyToken,
        tokenResponse?.uid
      );
      this.loginAuthenticationHelperService.setPromptCookie(
        userInputFields,
        this.authenticationConfig
      );
      if (
        this.loginAuthenticationHelperService.checkForAccessToken(tokenResponse)
      ) {
        this.getDashBrdDataBffCall(
          userInputFields,
          tokenResponse?.access_token,
          this.authenticationConfig,
          this.fromApplication,
          this.standalonePage
        );
      } else {
        this.updateLoaderAndDisableButton(false);
        this.displayPingError(tokenResponse);
        this.contentBodyCoreAdobeAnalyticsUtil.setLoginGlobalStatus(false);
      }
    } else {
      this.loginDasboardService
        .loginQueryPage()
        .subscribe(async (response: { queryParams: string }) => {
          if (response.queryParams) {
            await this.custLoginBffCall(
              userInputFields,
              response.queryParams,
              this.standalonePage
            );
          }
        });
    }
  }

  async onFormSubmit(
    submit = false,
    userInputFields?: LoginAuthUserInputFields
  ) {
    if (submit) {
      LoginFormValidate.touchControlsOnSubmit(this.getContent());
      this.getContent().loginFormAttributes.submitAttempt = true;
      if (this.getContent().formGroup.valid) {
        this.dataLayerServcie.updateUserNameAsLoginType(userInputFields);
        this.updateLoaderAndDisableButton(true);
        this.loginPostWithBFF(userInputFields);
      } else {
        this.updateErrorCountAndDisplayAlertBanner();
      }
    }
  }

  sendTokenServiceWorker(tokenResponse: {
    access_token: string;
    expires_in: string;
  }) {
    if (
      this.authenticationConfig?.ENABLE_NEW_PERSISTENT_LOGIN &&
      navigator.serviceWorker &&
      navigator.serviceWorker.controller
    ) {
      this.coreUtilService.sendMessage({
        type: 'ADD_TOKEN',
        token: tokenResponse?.access_token,
        issueTime: new Date().getTime(),
        expiresIn: parseInt(tokenResponse?.expires_in)
      });
      this.coreUtilService.sendMessage({ type: 'DELETE_STATUS' });
    }
  }

  getStoredRmeCookieHelper(dataObj: Partial<DashboardDataResponse>) {
    if (dataObj.rememberMeLastName) {
      this.getContent().formGroup.controls['userId'].setValue({
        value: dataObj.rememberMeID
      });
      this.getContent().formGroup.controls['keepMeLoggedIn'].setValue({
        value: dataObj.rememberMeIndicator
      });
      this.getContent().formGroup.controls['lastName'].setValue({
        value: dataObj.rememberMeLastName
      });
    } else {
      this.getContent().formGroup.controls['userId'].setValue({
        value: dataObj.rememberMeID
      });
      this.getContent().formGroup.controls['keepMeLoggedIn'].setValue({
        value: dataObj.rememberMeIndicator
      });
    }
  }

  getStoredRmeCookie() {
    if (
      this.coreUtilService.getLocalStorageWithExpiry('rme-ui') &&
      !this.authenticationConfig.PING_FLOW_ENABLED
    ) {
      this.loginDasboardService
        .getVsDashboardResponse()
        .subscribe((data: Partial<DashboardDataResponse>) => {
          const dataObj: Partial<DashboardDataResponse> =
            data as Partial<DashboardDataResponse>;
          if (dataObj && dataObj.rememberMeID && dataObj.rememberMeIndicator) {
            this.getStoredRmeCookieHelper(dataObj);
          }
        });
    }
  }
}
